import qs from 'qs'

import type { definitions, paths } from '@luxuryescapes/contract-svc-accommodation'

import request from 'api/requestUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'

import mapPropertyAnytimeRate from './mappers/propertyAnytimeRateMapper'
import mapProperty from './mappers/propertyMapper'
import mapPropertyRates from './mappers/propertyRatesMapper'

interface PropertyParams {
  regionCode: string
  currency: string
  previewMode: boolean
  canBookAllFlights?: boolean
  /**
   * When true, requests the `custom-default` copy mode — custom content is preferred over the
   * default source for each field. Controlled by the `cro_lpp_custom_content_v2` experiment.
   */
  useContentVariant?: boolean
}

// NB: We key off `paths` rather than `actions` to ensure we're tightly coupled with the endpoint's
// actual type.
type ServerGetPropertyContent = paths['/api/accommodation/content/properties/{propertyId}']['get']
// NB: `brand` parameter is supplied by `brandLoader` middleware.
type ServerGetPropertyContentRequestQueryParams = Omit<ServerGetPropertyContent['parameters']['query'], 'brand'>
type ServerGetPropertyContentResponse = ServerGetPropertyContent['responses']['200']['content']['application/json']

export async function getProperty(
  propertyId: string,
  params: PropertyParams,
): Promise<App.Property.Property | undefined> {
  const queryParams: ServerGetPropertyContentRequestQueryParams = {
    region: params.regionCode,
    currency: params.currency,
    copyMode: params.useContentVariant ? 'custom-default' : undefined,
    allowMysteryRooms: true,
  }
  const query = qs.stringify(queryParams)

  const response = await request.get<ServerGetPropertyContentResponse>(
    `/api/accommodation/content/properties/${propertyId}?${query}`,
  )
  const serverPropertyContent = response.result
  if (serverPropertyContent) {
    return mapProperty(serverPropertyContent, params.previewMode, params.canBookAllFlights)
  }
}

interface PropertyAnytimeRateParams {
  regionCode: string
  currency: string
}

// NB: We key off `paths` rather than `actions` to ensure we're tightly coupled with the endpoint's
// actual type.
type ServerAnytimePricing = paths['/api/accommodation/properties/anytime-pricing']['get']
// NB: `brand` parameter is supplied by `brandLoader` middleware.
type ServerAnytimePricingRequestQueryParams = Omit<ServerAnytimePricing['parameters']['query'], 'brand'>
type ServerAnytimePricingResponse = ServerAnytimePricing['responses']['200']['content']['application/json']

export async function getPropertyAnytimeRate(
  propertyId: string,
  params: PropertyAnytimeRateParams,
): Promise<[App.Property.AnytimeRate, App.Property.AnytimePromotionRate | undefined] | undefined> {
  const queryParams: ServerAnytimePricingRequestQueryParams = {
    region: params.regionCode,
    currency: params.currency,
    propertyIds: [propertyId],
  }
  const query = qs.stringify(queryParams)

  const response = await request.get<ServerAnytimePricingResponse>(
    `/api/accommodation/properties/anytime-pricing?${query}`,
  )
  const serverAnytimePricingForProperty = response.result.properties[propertyId]
  if (serverAnytimePricingForProperty) {
    return mapPropertyAnytimeRate(serverAnytimePricingForProperty, params.currency)
  }
}

interface PropertyRatesParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  regionCode: string
  currency: string
  previewMode: boolean
  source: string
  dynamicAvailability: boolean
  upliftRatesWithInclusions: boolean | 'lpc'
  enablePropertyCollect: boolean
  includeBundles: boolean
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  areFlightsEnabled: boolean
}

// NB: We key off `paths` rather than `actions` to ensure we're tightly coupled with the endpoint's
// actual type.
type ServerUnifiedAvailability = paths['/api/accommodation/properties/availability']['get']
// NB: `brand` parameter is supplied by `brandLoader` middleware.
type ServerUnifiedAvailabilityRequestQueryParams = Omit<ServerUnifiedAvailability['parameters']['query'], 'brand'>
type ServerUnifiedAvailabilityResponse = ServerUnifiedAvailability['responses']['200']['content']['application/json']
type ServerValidateBundleRate = paths['/api/accommodation/properties/availability/bundles/validate']['post']
type ServerValidateBundleRateRequestBody = Omit<definitions['ValidateBundleRateBody'], 'brand'>
type ServerValidateBundleRateResponse = ServerValidateBundleRate['responses']['200']['content']['application/json']

const GHA_ROOM_RATE_ID_PATTERN = /^(.+?)(?:_([A-Z]{3}))?$/

function parseGhaRoomRateId(roomRateId: string | undefined): [string | undefined, string | undefined] {
  if (!roomRateId) return [undefined, undefined]
  const result = GHA_ROOM_RATE_ID_PATTERN.exec(roomRateId)
  if (!result) return [undefined, undefined]
  return [result[1], result[2]]
}

function mapHighlightedReason(
  utmContent: string | undefined,
): ServerUnifiedAvailabilityRequestQueryParams['highlightedReason'] {
  switch (utmContent) {
    case 'gha-lpc':
    case 'gha-lpp':
      return utmContent
  }
}

export async function getPropertyRates(
  propertyId: string,
  params: PropertyRatesParams,
): Promise<Array<App.RawPropertyRoomRates>> {
  const [highlightedRateId, highlightedRateCurrency] = parseGhaRoomRateId(params.roomRateId)
  const queryParams: ServerUnifiedAvailabilityRequestQueryParams = {
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    occupancies: params.occupancies.map(generateOccupancyStringByRoom),
    region: params.regionCode,
    currency: params.currency,
    preview: params.previewMode,
    source: params.source,
    dynamicAvailability: params.dynamicAvailability,
    upliftRatesWithInclusions: params.upliftRatesWithInclusions,
    enablePropertyCollect: params.enablePropertyCollect,
    allowMysteryRooms: true,
    includeBundles: params.includeBundles,
    propertyIds: [propertyId],
    highlightedReason: mapHighlightedReason(params.utmContent),
    highlightedRoomId: params.roomTypeId,
    highlightedRateId,
    highlightedRateCurrency,
    includeUnavailableResults: true,
  }
  const query = qs.stringify(queryParams)

  const response = await request.get<ServerUnifiedAvailabilityResponse>(
    `/api/accommodation/properties/availability?${query}`,
    { credentials: 'include' },
  )
  const serverAvailabilityResponseProperty = response.result.properties[propertyId]
  if (serverAvailabilityResponseProperty) {
    return mapPropertyRates(serverAvailabilityResponseProperty, params.currency, params.areFlightsEnabled)
  }
  return EmptyArray
}

interface ValidatePropertyRateBundleParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  regionCode: string
  currency: string
  source: string
  dynamicAvailability: boolean
  propertyId: string
  rateId: string
  tickets: Array<{ experienceId: string; productId: string }>
  bundleId: string
}

export async function validatePropertyRateBundle(params: ValidatePropertyRateBundleParams): Promise<boolean> {
  const body: ServerValidateBundleRateRequestBody = {
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    occupancies: params.occupancies.map(generateOccupancyStringByRoom),
    region: params.regionCode,
    currency: params.currency,
    source: params.source,
    dynamicAvailability: params.dynamicAvailability,
    propertyId: params.propertyId,
    rateId: params.rateId,
    tickets: params.tickets,
    bundleId: params.bundleId,
  }

  const response = await request.post<ServerValidateBundleRateResponse, ServerValidateBundleRateRequestBody>(
    '/api/accommodation/properties/availability/bundles/validate',
    body,
    { credentials: 'include' },
  )

  return !!response?.result
}
